<template>
  <transition-group
    ref="bundleContainer"
    :class="{
      [$style.bundle]: true,
      [$style.bundleAnimated]: animated
    }"
    :enter-class="$style.bundleItemEnter"
    :leave-to-class="$style.bundleItemLeaveTo"
    :leave-active-class="$style.bundleItemLeaveActive"
    tag="div"
  >
    <img
      v-for="image of sortedItemImages"
      :key="image.key"
      :src="image.url"
      :class="{
        [$style.bundleItem]: true,
        [$style.burgerWrapper]: image.isBurgerWrapper
      }"
      :style="image.style"
      alt=""
    >
  </transition-group>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { ItemImageTypes } from '@/apps/menu/constants';
import helpers from '@/lib/helpers';

/**
 * Sums elements in a provided array.
 * @param {Array<number>} items
 */
const sum = items => items.reduce((acc, i) => acc + i, 0);

@Component({
  name: 'BurgerPreview',
})
export default class extends Vue {
  /** @type {Array} */
  @Prop({
    type:     Array,
    required: true,
  })
  items;

  /** @type {Array} */
  sortedItemImages = [];

  /** @type {ResizeObserver} */
  burgerContainerResizeObserver;

  animated = false

  mounted() {
    // In order to recalculate the burger styles.
    this.burgerContainerResizeObserver = new ResizeObserver(async (event) => {
      if (event[0].target.clientWidth > 0) {
        this.rerenderItemImages();

        // To prevent initial burger animation
        setTimeout(() => {
          this.animated = true;
        }, 100);
      } else {
        this.animated = false;
      }
    });

    this.burgerContainerResizeObserver.observe(this.$refs.bundleContainer.$el);
  }

  unmounted() {
    this.burgerContainerResizeObserver.disconnect();
  }

  @Watch('items')
  rerenderItemImages() {
    const container = this.$refs.bundleContainer
      ? this.$refs.bundleContainer.$el
      : null;

    if (container) {
      this.sortedItemImages = this.getStyledImages(container);
    }
  }

  getSortedImages() {
    return this.items
      .slice()
      .map(this.getItemImages)
      .flat()
      .filter(item => item != null)
      .sort((left, right) => right.displayIndex - left.displayIndex);
  }

  getStyledImages(container) {
    const itemImages = this.getSortedImages().map((image, idx, array) => ({
      ...image,

      // No need to set negative margin of a last item in case there is no burger wrapper in order to avoid picture shift
      offsetY:
        idx === array.length - 1 && !image.isBurgerWrapper ? 0 : image.offsetY,
    }));

    const {
      clientWidth: containerWidth,
      clientHeight: containerHeight,
    } = container;

    if (containerWidth === 0 || containerHeight === 0) {
      return [];
    }


    // In case some of the dimensions is more than a container, take the one that's bigger
    const maxScale = Math.max(
      sum(itemImages.map(i => i.height / containerHeight)) +
        sum(itemImages.map(i => i.offsetY / containerHeight)),
      Math.max(...itemImages.map(i => i.width / containerWidth))
    );

    return itemImages.map((image, i) => ({
      style: {
        height:    `${image.height / maxScale}px`,
        width:     `${image.width / maxScale}px`,
        marginTop: `${image.offsetY / maxScale}px`,
        zIndex:    i,
      },
      url:             helpers.getImagePath(image.url),
      key:             image.key,
      isBurgerWrapper: image.isBurgerWrapper,
    }));
  }

  /**
   * Returns a meta-information of an image based on the current type of display.
   * @returns Image object. May be `undefined` in case the preview is not available for this type of product in such a scenario.
   */
  getItemImages(item) {
    return (
      item.images
        .filter(image =>
          image.type === ItemImageTypes.configuratorBurgerPreviewImage)
        // In case quantity is > 1, we need to duplicate the images
        .map(image => Array(item.quantity).fill(image))
        .flat()
        .map((image, i) => ({
          ...image,
          key:             `${i}-${item.key}`,
          isBurgerWrapper: item.isBurgerWrapper,
        }))
    );
  }
}
</script>
<style module>
.bundle {
  height: 100%;
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  justify-content: center;
  align-items: center;
}

.bundleAnimated .bundleItem {
  transition: all 1s;
}

.bundleItemEnter,
.bundleItemLeaveTo {
  opacity: 0;
}

.bundleAnimated .bundleItemLeaveActive {
  position: absolute;

  /* Hiding this element immediately since
   it jumps back to `margin-top: auto` which is looks confusing */
  transition: all 0ms;
  opacity: 0;
}

.burgerWrapper.bundleItemEnter {
  transform: translateX(50px);
}
</style>



// WEBPACK FOOTER //
// src/apps/configurator/components/Preview/BurgerPreview.vue